import React, { useEffect, useRef, useState } from 'react';

export const GOOGLE_WEB_CLIENT_ID =
    '80195849052-g354619gb9nithq0nqhaapl94g51u9hv.apps.googleusercontent.com';

let googleScript: Promise<any>;
function loadGoogle() {
    const sdk = (window as any).google?.accounts?.id;
    if (sdk) return Promise.resolve(sdk);
    if (!googleScript) {
        googleScript = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.onload = () => resolve((window as any).google.accounts.id);
            script.onerror = () => {
                googleScript = undefined;
                script.remove();
                reject(
                    new Error(
                        'Google sign-in is unavailable. Use email instead.',
                    ),
                );
            };
            document.head.appendChild(script);
        });
    }
    return googleScript;
}

export function GoogleSignInButton({
    clientId = GOOGLE_WEB_CLIENT_ID,
    nonce,
    onCredential,
    disabled = false,
    portalStyle = false,
}: {
    clientId?: string;
    nonce?: string;
    onCredential: (credential: string) => void;
    disabled?: boolean;
    portalStyle?: boolean;
}) {
    const host = useRef<HTMLDivElement>(null);
    const callback = useRef(onCredential);
    const blocked = useRef(disabled);
    callback.current = onCredential;
    blocked.current = disabled;
    const [error, setError] = useState('');
    useEffect(() => {
        let active = true;
        let resize: (() => void) | undefined;
        loadGoogle()
            .then((sdk) => {
                if (!active || !host.current) return;
                sdk.initialize({
                    client_id: clientId,
                    nonce,
                    auto_select: false,
                    callback: (response: any) => {
                        if (active && !blocked.current && response.credential)
                            callback.current(response.credential);
                    },
                });
                const render = () => {
                    if (!active || !host.current) return;
                    host.current.replaceChildren();
                    sdk.renderButton(host.current, {
                        theme: 'outline',
                        shape: 'rectangular',
                        width: portalStyle
                            ? Math.min(313, host.current.clientWidth || 313)
                            : undefined,
                        size: 'large',
                        text: 'signin_with',
                    });
                };
                render();
                if (portalStyle) {
                    resize = render;
                    window.addEventListener('resize', resize);
                }
            })
            .catch((e) => {
                if (active) setError(e.message);
            });
        return () => {
            active = false;
            if (resize) window.removeEventListener('resize', resize);
        };
    }, [clientId, nonce, portalStyle]);
    return (
        <div
            className={portalStyle ? 'portal-google-signin' : undefined}
            aria-disabled={disabled}
            style={{ pointerEvents: disabled ? 'none' : undefined }}
        >
            <div ref={host} />
            {error && <p role="status">{error}</p>}
        </div>
    );
}

type InputProps = Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'value'
> & {
    value: string;
    onValueChange: (value: string) => void;
};
export function EmailAddressInput({
    value,
    onValueChange,
    ...props
}: InputProps) {
    return (
        <input
            type="email"
            autoComplete="email"
            maxLength={254}
            aria-label="Email address"
            {...props}
            value={value || ''}
            onChange={(event) => onValueChange(event.target.value)}
        />
    );
}
export function OTPInput({ value, onValueChange, ...props }: InputProps) {
    return (
        <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            aria-label="6-digit code"
            {...props}
            value={value || ''}
            onChange={(event) =>
                onValueChange(event.target.value.replace(/\D/g, '').slice(0, 6))
            }
        />
    );
}
